// src/pages/DashboardPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useAuthContext } from "../context";
import Sidebar from "../components/Modal/Sidebar";
import SearchModal from "../components/Modal/SearchModal";
import UserCard from "../components/Modal/UserCard";

const normalizeGender = (value) => {
  if (value === "Man") return "Male";
  if (value === "Woman") return "Female";
  return value;
};

export default function DashboardPage() {
  const { allUsers, fetchAllUsers, user } = useAuthContext();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [recentMatches, setRecentMatches] = useState([]);
  const [nearbyUsers, setNearbyUsers] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [allMatches, setAllMatches] = useState([]);
  const [allNearby, setAllNearby] = useState([]);

  const isGold = user?.membership_type === "gold" && user?.membership_status;

  const [featuredGoldUsers, setFeaturedGoldUsers] = useState([]);

  useEffect(() => {
    if (!user || loading) return;
  
    const featured = allUsers.filter(
      (u) =>
        u.user_id !== user.user_id && 
        u.membership_type === "gold" &&
        u.membership_status === true &&
        u.profile_image
    );
  
    setFeaturedGoldUsers(featured.slice(0, 6));
  }, [loading, allUsers, user]);  

  const scrollToTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  // 1) fetch user list once
  useEffect(() => {
    (async () => {
      try {
        await fetchAllUsers();
      } catch (err) {
        console.error("Error loading users:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchAllUsers]);

  useEffect(() => {
    if (loading || !user) return;
  
    const myGender = normalizeGender(user.gender);
    const myInterest = normalizeGender(user.interestedIn);
  
    const matches = allUsers.filter((u) => {
      if (u.user_id === user.user_id) return false;
      return (
        normalizeGender(u.gender) === myInterest &&
        normalizeGender(u.interestedIn) === myGender
      );
    });
  
    const nearby = allUsers.filter((u) => {
      if (u.user_id === user.user_id) return false;
      return u.state === user.state;
    });
  
    setAllMatches(matches);
    setAllNearby(nearby);
  }, [loading, allUsers, user]);
  
  const PREVIEW_LIMIT_regular = 3;
  const PREVIEW_LIMIT_gold = 4;

  const PREVIEW_LIMIT = isGold ? PREVIEW_LIMIT_gold : PREVIEW_LIMIT_regular;
  
  useEffect(() => {
    if (!user || loading) return;
  
    const PREVIEW_LIMIT = isGold ? PREVIEW_LIMIT_gold : PREVIEW_LIMIT_regular;
  
    if (allMatches.length > 0) {
      //console.log("Slicing matches:", allMatches.length, "Gold:", isGold);
      setRecentMatches(allMatches.slice(0, PREVIEW_LIMIT));
    }
  }, [user, loading, allMatches, isGold]); // include isGold for reactivity
  
  useEffect(() => {
    if (!user || loading) return;
  
    const PREVIEW_LIMIT = isGold ? PREVIEW_LIMIT_gold : PREVIEW_LIMIT_regular;
  
    if (allNearby.length > 0) {
      setNearbyUsers(allNearby.slice(0, PREVIEW_LIMIT));
    }
  }, [user, loading, allNearby, isGold]); // include isGold for reactivity  

  // shareable search-state objects
  const matchesQuery = {
    ageRange: [18, 99],
    gender: normalizeGender(user.gender),
    lookingFor: normalizeGender(user.interestedIn),
    country: "",
    state: "",
    city: "",
  };
  
  const nearbyQuery = {
    ageRange: [18, 99],
    gender: "",
    lookingFor: "",
    country: "",      // or user.country if desired
    state: user.state,
    city: ""
  };

  const handleSearchUpdate = (q) => {
    localStorage.setItem("searchQuery", JSON.stringify(q));
    setShowSearchModal(false);
    navigate("/search", { state: q });
  };

  return (
    <>
     <Helmet>
  <title>Dashboard – SomeOne4u</title>
  <meta
    name="description"
    content="Welcome to your SomeOne4u dashboard. View your profile, connect with others, and manage your activity in one place."
  />
  <meta
    name="keywords"
    content="SomeOne4u dashboard, user profile, social platform, connections"
  />
  <link rel="canonical" href="https://someone4u.com/dashboard" />

  <meta property="og:title" content="Dashboard – SomeOne4u" />
  <meta
    property="og:description"
    content="Access your personalized SomeOne4u dashboard to manage your profile, interactions, and connections."
  />
  <meta property="og:url" content="https://someone4u.com/dashboard" />
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="SomeOne4u" />
</Helmet>

      <div className="min-h-screen flex bg-gray-50">
        {/* Sidebar */}
        <Sidebar onSearch={() => setShowSearchModal(true)} />

        {/* Main content */}
        <div className="flex-1 flex flex-col">
          {/* Hero bar */}
          <header className="bg-white shadow p-6 flex items-center justify-between">
            <h1 className="text-2xl font-bold">
              Hello, <span className="text-red-400">{user.firstName} {user.lastName}</span>
            </h1>
          </header>

          {/* Carousels */}
          <main className="flex-1 overflow-y-auto p-6 space-y-10">

          {featuredGoldUsers.length > 0 && (
  <section>
    <h2 className="text-xl font-semibold mb-6">✨ Featured Singles ✨</h2>
    <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
      {featuredGoldUsers.map((u) => (
        <div
          key={u.user_id}
          className="bg-white rounded-xl shadow hover:shadow-lg transition cursor-pointer p-6 text-center relative"
          onClick={() => {
            navigate(`/user/${u.user_id}`);
            scrollToTop();
          }}
        >
          {/* Gold Member badge */}
          <span className="absolute top-3 right-3 bg-yellow-400 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
            🌟 Gold Member
          </span>

          {/* Circular Profile Image */}
          <div className="w-24 h-24 mx-auto mb-4 rounded-full border-4 border-yellow-400 overflow-hidden">
            <img
              src={u.profile_image || "/image/operator.png"}
              alt={`${u.firstName} ${u.lastName}`}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Name and Age */}
          <h3 className="text-lg font-semibold text-gray-800">
            {u.firstName}, {u.age}
          </h3>

          {/* Location */}
          <p className="text-sm text-gray-500">
            {u.city}, {u.state}, {u.country}
          </p>

          {/* View Profile Button */}
          <button
            className="mt-4 bg-blue-500 hover:bg-blue-600 text-white text-sm px-4 py-2 rounded-full font-medium"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/user/${u.user_id}`);
              scrollToTop();
            }}
          >
            View Profile
          </button>
        </div>
      ))}
    </div>
  </section>
)}


          <CarouselSection
  title="Recent Matches"
  items={recentMatches}
  loading={loading}
  onItemClick={(u) => navigate(`/user/${u.user_id}`)}
  emptyText="No matches yet."
  showViewAll={allMatches.length > PREVIEW_LIMIT}
  onViewAll={() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate("/search", { state: matchesQuery });
  }}
  viewAllText={`View all ${allMatches.length} matches →`}
/>
<CarouselSection
  title="People Nearby"
  items={nearbyUsers}
  loading={loading}
  onItemClick={(u) => navigate(`/user/${u.user_id}`)}
  emptyText="No one nearby."
  showViewAll={allNearby.length > PREVIEW_LIMIT}
  onViewAll={() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate("/search", { state: nearbyQuery });
  }}  
  viewAllText={`View all ${allNearby.length} nearby →`}
/>
            {!isGold && (
              <div className="text-center flex flex-col items-center space-y-4">
                <p className="text-gray-700">
                  Upgrade to <strong>Gold</strong> to unlock all profiles.
                </p>
                <button
                   onClick={() => {
                    navigate("/membership");
                    scrollToTop();
                  }}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-8 py-2 rounded-full font-medium shadow"
                >
                  Upgrade to Gold
                </button>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Search Modal */}
      {showSearchModal && (
        <SearchModal
          initialAge={[18, 45]}
          initialGender=""
          initialLookingFor=""
          initialCity=""
          initialState=""
          onSearchUpdate={handleSearchUpdate}
          onClose={() => setShowSearchModal(false)}
        />
      )}
    </>
  );
}

// Horizontal carousel helper
function CarouselSection({
  title,
  items,
  loading,
  onItemClick,
  emptyText,
  showViewAll = false,
  onViewAll,
  viewAllText = "View all →",
}) {
  return (
    <section>
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-10 w-10 border-4 border-red-400 rounded-full border-t-transparent" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-gray-600">{emptyText}</p>
      ) : (
        <>
          <div
            className="grid gap-6"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            }}
          >
            {items.map((u) => (
              <UserCard key={u.user_id} user={u} onClick={onItemClick} />
            ))}
          </div>

          {showViewAll && (
            <div className="text-center mt-4">
              <button
                onClick={onViewAll}
                className="text-red-500 hover:underline font-medium"
              >
                {viewAllText}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

